import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { fetchAdminDataByEmail } from "../api";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext<any | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [adminData, setAdminData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (isInitial = false) => {
    try {
      if (isInitial) setIsLoading(true);
      else setIsRefreshing(true);

      setError(null);
      const data = await fetchAdminDataByEmail();

      if (data) {
        setAdminData(data);
      } else {
        setError("No admin data found");
      }
    } catch (err) {
      setError("Failed to fetch admin data");
      console.error("Error fetching admin data:", err);
    } finally {
      if (isInitial) setIsLoading(false);
      else setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData(true);
  }, []);

  const refreshAdminData = async () => {
    await fetchData(false);
  };

  const value: any = {
    adminData,
    isLoading,
    isRefreshing,
    error,
    refreshAdminData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthData = (): any => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthLoader: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isLoading, error } = useAuthData();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-base">Loading...</div>
      </div>
    );
  }

  if (error) {
    navigate("/login");
    localStorage.removeItem("__Iplot_admin_access_");
  }

  return <>{children}</>;
};
